// AGC: Field and Plot types for DroidFarm
import type { LotGeocoding } from './geocoding'

/**
 * AGC: Crop types available - Complete list of Brazilian grains and crops
 * AGC: Based on EMBRAPA, CONAB, and Brazilian agricultural production data
 */
export type CropType = 
  // AGC: Major grains
  | 'soja'           // Soybean - largest production
  | 'milho'          // Corn
  | 'milho_safrinha' // Second crop corn
  | 'trigo'          // Wheat
  | 'arroz'          // Rice
  | 'feijao'         // Common beans
  | 'feijao_carioca' // Carioca beans
  | 'feijao_preto'   // Black beans
  // AGC: Other grains
  | 'sorgo'          // Sorghum
  | 'aveia'          // Oats
  | 'cevada'         // Barley
  | 'centeio'        // Rye
  | 'triticale'      // Triticale
  | 'milheto'        // Pearl millet
  // AGC: Oilseeds
  | 'algodao'        // Cotton
  | 'girassol'       // Sunflower
  | 'amendoim'       // Peanut
  | 'canola'         // Canola
  | 'mamona'         // Castor bean
  | 'gergelim'       // Sesame
  | 'linhaca'        // Flaxseed
  // AGC: Sugar & fiber
  | 'cana'           // Sugarcane
  // AGC: Perennial crops
  | 'cafe'           // Coffee
  | 'cacau'          // Cocoa
  | 'laranja'        // Orange
  // AGC: Vegetables & tubers
  | 'mandioca'       // Cassava
  | 'batata'         // Potato
  // AGC: Legumes for cover/rotation
  | 'crotalaria'     // Crotalaria (cover crop)
  | 'nabo_forrageiro'// Forage turnip (cover crop)
  | 'ervilhaca'      // Vetch (cover crop)
  | 'brachiaria'     // Brachiaria (pasture/cover)
  // AGC: Other
  | 'outros'

/**
 * AGC: Growth stage of a crop
 */
export type GrowthStage = 
  | 'plantio'
  | 'germinacao'
  | 'vegetativo'
  | 'floracao'
  | 'frutificacao'
  | 'maturacao'
  | 'colheita'

/**
 * AGC: Health status of a crop
 */
export type HealthStatus = 'excelente' | 'bom' | 'regular' | 'ruim' | 'critico'

/**
 * AGC: Management status of a lot
 */
export type ManagementStatus = 
  | 'por-semear'
  | 'semeado'
  | 'em-desenvolvimento'
  | 'pronto-colher'

/**
 * AGC: Soil type
 */
export type SoilType = 'argiloso' | 'arenoso' | 'franco' | 'siltoso' | 'organico'

/**
 * AGC: Financial data for a plot/lot
 */
export interface PlotFinancialData {
  projectedRevenue: number // AGC: R$
  projectedCost: number // AGC: R$
  executedCost: number // AGC: R$
  executedCostPercentage: number // AGC: % of projected cost
  currentPrice: number // AGC: R$ per unit (saca, ton, etc.)
  projectedYield: number // AGC: sacas/ha or tons/ha
  priceUnit: 'saca' | 'tonelada' // AGC: Unit for pricing
}

/**
 * AGC: Plot/Talhão/Lote data structure (Lot)
 * AGC: A Lot belongs to a Farm (Field is alias for Farm)
 */
export interface Plot {
  id: string
  fieldId: string // AGC: Farm ID (kept as fieldId for backward compatibility)
  farmId?: string // AGC: Optional farmId alias (for clarity)
  name: string
  area: number // AGC: Area in hectares
  perimeter?: number // AGC: Perimeter in meters
  cropType: CropType
  variety?: string
  plantingDate?: string
  expectedHarvestDate?: string
  growthStage: GrowthStage
  healthStatus: HealthStatus
  managementStatus: ManagementStatus // AGC: Current management status
  managementProgress: number // AGC: % of management tasks completed
  coordinates: [number, number][] // AGC: Polygon coordinates [lat, lng]
  soilType?: SoilType
  irrigated: boolean
  yieldEstimate?: number // AGC: kg/ha
  financial?: PlotFinancialData // AGC: Financial data
  notes?: string
  createdAt: string
  updatedAt: string
  // AGC: Soft delete / archive functionality
  isArchived?: boolean // AGC: If true, lot is archived (soft deleted)
  archivedAt?: string | null // AGC: ISO timestamp when archived, null if not archived
  safraStartDate?: string  // AGC: ISO date — set by user to start SAFRA weather tracking
  geocoding?: LotGeocoding
}

/**
 * AGC: Farm (Fazenda/Campo) data structure
 * AGC: A Farm contains multiple Lots (Talhões/Lotes)
 * AGC: Note: Field is kept as alias for backward compatibility, but domain terminology is Farm
 */
export interface Farm {
  id: string
  name: string
  location: string
  totalArea: number // AGC: Total area in hectares
  plots: Plot[] // AGC: Lots (Talhões/Lotes) - kept as plots for backward compatibility
  lots?: Plot[] // AGC: Optional lots alias (for clarity)
  coordinates: [number, number] // AGC: Center coordinates [lat, lng]
  createdAt: string
  updatedAt: string
}

/**
 * AGC: Field is an alias for Farm (backward compatibility)
 * AGC: Domain terminology: Farm → Lots hierarchy
 * AGC: UI should treat Field as Farm and Plot as Lot
 */
export type Field = Farm

/**
 * AGC: Crop type info for display
 */
export interface CropTypeInfo {
  id: CropType
  name: string
  color: string
  icon: string
  imageUrl?: string          // AGC: Full HD crop photo URL (for LOD card backgrounds)
  imageUpdatedAt?: string    // AGC: ISO timestamp for cache invalidation
}

/**
 * AGC: Crop type configurations - Complete list with icons and colors
 */
export const CROP_TYPES: CropTypeInfo[] = [
  // AGC: Major grains
  { id: 'soja', name: 'Soja', color: '#10B981', icon: '🫘' },
  { id: 'milho', name: 'Milho', color: '#F59E0B', icon: '🌽' },
  { id: 'milho_safrinha', name: 'Milho Safrinha', color: '#D97706', icon: '🌽' },
  { id: 'trigo', name: 'Trigo', color: '#B45309', icon: '🌾' },
  { id: 'arroz', name: 'Arroz', color: '#F5F5DC', icon: '🍚' },
  { id: 'feijao', name: 'Feijão', color: '#7C3AED', icon: '🫘' },
  { id: 'feijao_carioca', name: 'Feijão Carioca', color: '#C4A484', icon: '🫘' },
  { id: 'feijao_preto', name: 'Feijão Preto', color: '#1F2937', icon: '🫘' },
  // AGC: Other grains
  { id: 'sorgo', name: 'Sorgo', color: '#DC2626', icon: '🌾' },
  { id: 'aveia', name: 'Aveia', color: '#F5DEB3', icon: '🌾' },
  { id: 'cevada', name: 'Cevada', color: '#D4A574', icon: '🌾' },
  { id: 'centeio', name: 'Centeio', color: '#8B7355', icon: '🌾' },
  { id: 'triticale', name: 'Triticale', color: '#C19A6B', icon: '🌾' },
  { id: 'milheto', name: 'Milheto', color: '#E5C07B', icon: '🌾' },
  // AGC: Oilseeds
  { id: 'algodao', name: 'Algodão', color: '#E5E7EB', icon: '☁️' },
  { id: 'girassol', name: 'Girassol', color: '#FBBF24', icon: '🌻' },
  { id: 'amendoim', name: 'Amendoim', color: '#C8A165', icon: '🥜' },
  { id: 'canola', name: 'Canola', color: '#FFEB3B', icon: '🌼' },
  { id: 'mamona', name: 'Mamona', color: '#8B0000', icon: '🌿' },
  { id: 'gergelim', name: 'Gergelim', color: '#F5DEB3', icon: '🌿' },
  { id: 'linhaca', name: 'Linhaça', color: '#8B4513', icon: '🌿' },
  // AGC: Sugar & fiber
  { id: 'cana', name: 'Cana-de-açúcar', color: '#84CC16', icon: '🎋' },
  // AGC: Perennial crops
  { id: 'cafe', name: 'Café', color: '#78350F', icon: '☕' },
  { id: 'cacau', name: 'Cacau', color: '#5C4033', icon: '🍫' },
  { id: 'laranja', name: 'Laranja', color: '#FF8C00', icon: '🍊' },
  // AGC: Vegetables & tubers
  { id: 'mandioca', name: 'Mandioca', color: '#D2B48C', icon: '🥔' },
  { id: 'batata', name: 'Batata', color: '#DEB887', icon: '🥔' },
  // AGC: Cover crops
  { id: 'crotalaria', name: 'Crotalária', color: '#228B22', icon: '🌿' },
  { id: 'nabo_forrageiro', name: 'Nabo Forrageiro', color: '#32CD32', icon: '🌿' },
  { id: 'ervilhaca', name: 'Ervilhaca', color: '#90EE90', icon: '🌿' },
  { id: 'brachiaria', name: 'Braquiária', color: '#6B8E23', icon: '🌿' },
  // AGC: Other
  { id: 'outros', name: 'Outros', color: '#6B7280', icon: '🌿' },
]

/**
 * AGC: Get crop display information (handles both predefined and custom crop types)
 * AGC: Returns name, icon, color, and imageUrl for display
 */
export const getCropDisplayInfo = (cropType: string | null | undefined | unknown): { name: string; icon: string; color: string; imageUrl?: string } => {
  // AGC: Handle null, undefined, or empty values
  if (!cropType) {
    return { name: 'N/A', icon: '🌿', color: '#6B7280' }
  }
  
  // AGC: Handle non-string types (objects, numbers, etc from Firebase)
  let cropTypeStr: string
  if (typeof cropType === 'string') {
    cropTypeStr = cropType
  } else if (typeof cropType === 'object' && cropType !== null) {
    // AGC: If it's an object, try to extract id, value, or name property
    const obj = cropType as Record<string, unknown>
    cropTypeStr = String(obj.id || obj.value || obj.name || 'N/A')
  } else {
    cropTypeStr = String(cropType)
  }
  
  // AGC: Handle empty string after conversion
  if (!cropTypeStr || cropTypeStr === 'N/A' || cropTypeStr === 'undefined' || cropTypeStr === 'null') {
    return { name: 'N/A', icon: '🌿', color: '#6B7280' }
  }
  
  // AGC: Try to find in predefined list
  const predefined = CROP_TYPES.find(c => c.id === cropTypeStr)
  if (predefined) {
    return { name: predefined.name, icon: predefined.icon, color: predefined.color, imageUrl: predefined.imageUrl }
  }
  
  // AGC: Custom/learned crop type - capitalize and use default icon
  const capitalized = cropTypeStr.charAt(0).toUpperCase() + cropTypeStr.slice(1).toLowerCase()
  return { name: capitalized, icon: '🌿', color: '#6B7280' }
}



